<!-- src/components/MeetingAudioView.vue -->
<template>
    <div class="app-container">
      <header-bar />
      <div class="content-wrapper">
        <div class="empty-left"></div>
        <div class="left-sidenotes">
          <div @click="$router.back()" class="sidenote">
            <div class="sidenote-outer-wrapper"> 
              <div class="sidenote-inner-wrapper">
                <img src="@/assets/left arrow icon.svg" alt="Previous Date" class="nav-arrow"/>
                Return to Summary
              </div>
            </div>
          </div>
        </div>
        <div class="main">
          <div v-if="loading">Loading meeting details...</div>
          <div v-else-if="error">{{ error }}</div>
          <div v-else>
            <div class="audio-details">
              <!-- Audio Player Section -->
              <div>
                <p class="header">Meeting Audio</p>
                <audio v-if="meeting.files?.audio" :src="audioUrl" controls class="audioPlayer"></audio>
                <p v-else>No audio recording available</p>
              </div>
  
              <!-- Transcript Section -->
              <div class="transcript-section">
                <p class="header">Meeting Transcript</p>
                <div v-if="parsedTranscript && parsedTranscript.length > 0">
                  <div
                    v-for="(monologue, index) in parsedTranscript"
                    :key="index"
                    class="monologue"
                  >
                    <div v-if="monologue.speaker !== undefined"
                         class="speaker-label">
                      Speaker {{ monologue.speaker }}:
                    </div>
                    <div class="elements">
                      <template v-for="(element, elementIndex) in monologue.elements"
                                :key="elementIndex">
                        <span v-if="element.type === 'text'" class="transcript-text">
                          {{ element.value }}
                        </span>
                        <span v-else-if="element.type === 'punct'" class="transcript-punct">
                          {{ element.value }}
                        </span>
                      </template>
                    </div>
                  </div>
                </div>
                <p v-else>No transcript available</p>
              </div>
            </div>
          </div>
        </div>
        <div class="right-sidenotes"></div>
      </div>
    </div>
  </template>
  
  <script>
  import HeaderBar from './RevisedHeader.vue';
  import { ref, onMounted, watch } from 'vue';
  import { useRoute } from 'vue-router';
  import { doc, getDoc } from 'firebase/firestore';
  import { db } from '@/firebase';
  import { getStorage, ref as storageRef, getDownloadURL } from 'firebase/storage';
  import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
  
  export default {
    name: 'MeetingAudioView',
    components: {
      HeaderBar,
    },
    setup() {
      const route = useRoute();
  
      const meeting = ref(null);
      const loading = ref(true);
      const error = ref(null);
      const audioUrl = ref('');
      const parsedTranscript = ref([]);
  
      const fetchMeetingDetails = async () => {
        loading.value = true;
        error.value = null;
  
        try {
          const meetingId = route.params.id;
          const meetingRef = doc(db, 'meetingInfo', meetingId);
          const docSnap = await getDoc(meetingRef);
  
          if (docSnap.exists()) {
            meeting.value = {
              id: docSnap.id,
              ...docSnap.data()
            };
          } else {
            error.value = 'Meeting not found';
          }
        } catch (err) {
          console.error('Error fetching meeting details:', err);
          error.value = 'Failed to load meeting details. Please try again later.';
        } finally {
          loading.value = false;
        }
      };
  
      const fetchAudioUrl = async () => {
        if (meeting.value?.files?.audio) {
          try {
            const storage = getStorage();
            const audioRef = storageRef(storage, meeting.value.files.audio);
            audioUrl.value = await getDownloadURL(audioRef);
          } catch (err) {
            console.error('Error loading audio:', err);
          }
        }
      };
  
      const fetchAndParseTranscript = async () => {
        if (meeting.value?.transcriptionStatus === 'chunked') {
          try {
            const chunksSnapshot = await getDocs(
              query(
                collection(db, 'transcriptionChunks'),
                where('parentDocId', '==', meeting.value.id),
                orderBy('chunkIndex')
              )
            );
  
            if (!chunksSnapshot.empty) {
              const assembledString = chunksSnapshot.docs.map((doc) => doc.data().data).join('');
              parsedTranscript.value = JSON.parse(assembledString).monologues;
            }
          } catch (err) {
            console.error('Error assembling transcription:', err);
            parsedTranscript.value = [];
          }
        } else if (meeting.value?.transcription && meeting.value.transcription !== 'No Data') {
          try {
            const transcriptData =
              typeof meeting.value.transcription === 'string'
                ? JSON.parse(meeting.value.transcription)
                : meeting.value.transcription;
  
            parsedTranscript.value = transcriptData.monologues;
          } catch (err) {
            console.error('Error parsing transcript:', err);
            parsedTranscript.value = [];
          }
        } else {
          parsedTranscript.value = [];
        }
      };
  
      onMounted(async () => {
        await fetchMeetingDetails();
        await fetchAudioUrl();
        await fetchAndParseTranscript();
      });
  
      watch(
        meeting,
        () => {
          fetchAudioUrl();
          fetchAndParseTranscript();
        },
        { deep: true }
      );
  
      return {
        meeting,
        loading,
        error,
        audioUrl,
        parsedTranscript,
      };
    },
  };
  </script>
  
  <style scoped>
  .app-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
  
  .content-wrapper {
    position: relative;
    flex: 1;
    display: flex;
    overflow: hidden;
    padding-top: 64px;
  }
  
  .empty-left,
  .right-sidenotes,
  .left-sidenotes {
    display: flex;
    flex: 1;
    align-items: flex-start;
    background-color: #fffff8;
  }
  
  .main {
    flex: 3;
    background-color: #fffff8;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    overflow: auto;
    max-width: 1200px;
  }
  
  /* Audio + Transcript Section Styles */
  .audio-details {
    padding: 1rem;
    max-width: 800px;
    margin: 0 auto;
  }
  
  .monologue {
    margin-bottom: 1rem;
  }
  
  .speaker-label {
    margin-bottom: 0.1rem;
  }
  
  .transcript-text {
    line-height: 1.5;
  }
  
  .transcript-punct {
    margin-right: 0.25rem;
  }
  
  audio {
    width: 100%;
    margin-bottom: 1rem;
  }
  
  .transcript-section {
    margin-top: 50px;
  }
  
  .header {
    background-color: #fffff8;
  }
  
  .audio-player {
    padding-top: 10px;
  }

  .nav-arrow{
    height: 14px;
    width: 14px;
    cursor: pointer;
  }
  
  :global(.sidenote) {
    position: relative;
    left: 5%;
    width: 80%;
    font-size: 1rem;
    line-height: 1.4;
    transition: transform 0.2s ease;
    z-index: 1;
    margin-top: 2rem;
  }

  :global(.sidenote:hover),
  :global(.sidenote.selected) {
    position: relative;
    left: 5%;
    width: 80%;
    font-size: 1rem;
    line-height: 1.4;
    transition: transform 0.2s ease;
    border-left: 3px solid #545051;
    border-radius: 3px;
    cursor: pointer;
    z-index: 1;
  }

  :global(.sidenote.hover),
  :global(.sidenote.selected)  {
    position: relative;
    left: 5%;
    width: 80%;
    font-size: 1rem;
    line-height: 1.4;
    transition: transform 0.2s ease;
    border-left: 3px solid #545051;
    border-radius: 3px;
  }

  :global(.sidenote-outer-wrapper) {
    position: relative;
    border-radius: 0px;
    background-color: #fffff8;
  }
  :global(.sidenote-inner-wrapper) {
    background-color: #fffff8;
    padding: .5rem;
    border-radius: 0px;
    border-top: 1px dotted #545051;
    border-bottom: 1px dotted #545051;
  }

  /* Responsive Design */
@media (max-width: 768px) {
  .empty-left,
  .right-sidenotes,
  .left-sidenotes {
    display: none;
  }

  .main {
    flex: 1;
    max-width: 100%;
    padding: 0 1rem; /* Optional: add padding for better readability */
  }

  .viz-area {
    overflow: auto;
  }
}

@media (min-width: 768px) {
  .empty-left, .left-sidenotes {
    display: none;
  }

  .right-sidenotes {
    display: flex;
  }

  .main {
    flex: 2;
    max-width: 100%;
    padding: 0 2rem;
  }
}

@media (min-width: 1024px) {
  .empty-left, .right-sidenotes, .left-sidenotes {
    display: flex;
  }

  .main {
    flex: 3;
    max-width: 1200px;
    padding: 0;
  }
}

  </style>
  